import { FC } from "react";

import { showAddSectionModal } from "../helpers";

import { showRenameCourseModal } from "./AddOrRenameCourseModalUtils";
import { CourseOptionsMenu } from "./CourseOptionsMenu";

import { LoadingScreen } from "components/LoadingScreen";
import { AdminSectionListPanel } from "components/materials/home/admin/content/AdminSectionList";
import { t } from "i18n/i18n";
import { AccordionTitle } from "mds/components/AccordionTitle";
import { Banner } from "mds/components/Banner";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { AddPlusIcon, ChevronRightIcon, FolderIcon, TextBooksIcon } from "mds/icons";
import { useAppDispatch } from "store/index";
import {
  courseGroupingSelectors,
  courseSelectors,
  selectSectionIdsByTermAndCourse,
  selectTermIdsWithSectionsByCourse,
  termSelectors,
} from "store/selectors";
import { localStateActions } from "store/slices/local";
import { useHasItemsSelector, useListSelector, useObjectSelector } from "store/store-hooks";
import { ADMIN_COURSE_EVENT_PREFIX } from "utils/amplitude";
import { getCourseDisplayTitle } from "utils/course";
import { useSectionCloneObserver } from "utils/hooks/use-section-clone-observer";

type CourseTermSectionPanelProps = {
  termId: string;
  courseId: string;
  canEdit: boolean;
  isDisabled?: boolean;
};

const CourseTermSectionPanel: FC<CourseTermSectionPanelProps> = ({
  termId,
  courseId,
  canEdit,
  isDisabled = false,
}) => {
  const term = useObjectSelector((s) => termSelectors.selectById(s, termId), ["title"]);
  const course = useObjectSelector((s) => courseSelectors.selectById(s, courseId), ["code"]);

  if (!term || !course) {
    return null;
  }

  return (
    <div className="course-panel ml-2">
      <AdminSectionListPanel
        canEdit={canEdit}
        courseId={courseId}
        eventPrefix={ADMIN_COURSE_EVENT_PREFIX}
        eventProperties={{ course: course.code, term: term.title }}
        isDisabled={isDisabled}
        termId={termId}
        title={term.title}
      />
    </div>
  );
};

type SectionDetailTermViewProps = {
  courseId: string;
  canEdit: boolean;
};

export const SectionDetailTermView: FC<SectionDetailTermViewProps> = ({ courseId, canEdit }) => {
  const course = useObjectSelector(
    (state) => courseSelectors.selectById(state, courseId),
    ["code", "title", "grouping_id"],
  );
  const dispatch = useAppDispatch();
  const hasSections = useHasItemsSelector((state) =>
    selectSectionIdsByTermAndCourse(state, null, courseId),
  );
  const activeTermIds = useListSelector((state) =>
    selectTermIdsWithSectionsByCourse(state, courseId, false),
  );
  const archivedTermIds = useListSelector((state) =>
    selectTermIdsWithSectionsByCourse(state, courseId, true),
  );
  const grouping = useObjectSelector(
    (s) => courseGroupingSelectors.selectById(s, course?.grouping_id),
    ["title"],
  );

  useSectionCloneObserver();

  const handleEnableEditMode = () => {
    dispatch(localStateActions.setIsEditMode(true));
  };

  if (!course) {
    return <LoadingScreen>{t("home.settings.no_sections")}</LoadingScreen>;
  }

  const handleAddSection = () =>
    showAddSectionModal({
      defaultCourseId: courseId,
      eventName: `${ADMIN_COURSE_EVENT_PREFIX}Add Section`,
    });

  return (
    <div className="px-0 py-3 md:px-7">
      <h2 className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-2">
          {grouping && (
            <div className="flex items-center gap-2">
              <FolderIcon className="mb-[3px] text-blue" size="l" />
              <span className="text-black-tint-20">{grouping.title}</span>
              <ChevronRightIcon />
            </div>
          )}

          <div className="flex items-center gap-2">
            <TextBooksIcon className="mt-[1px] text-blue" size="l" />
            <div className="h2">{getCourseDisplayTitle(course)}</div>
          </div>
        </div>

        {canEdit && (
          <div className="flex items-center gap-2">
            <SquareButton
              eventName={`${ADMIN_COURSE_EVENT_PREFIX}Add Section`}
              kind="secondary"
              size="s"
              onClick={handleAddSection}
            >
              <IconText iconStart={<AddPlusIcon />} text={t("common.add.section")} />
            </SquareButton>

            <CourseOptionsMenu
              courseId={courseId}
              kind="secondary"
              onRename={() => showRenameCourseModal(courseId)}
            />
          </div>
        )}
      </h2>

      {!hasSections ? (
        <Banner
          actionButtonText={canEdit ? t("common.add.section") : t("warning.enable_edit_mode")}
          eventName={
            canEdit
              ? `${ADMIN_COURSE_EVENT_PREFIX}Add Section`
              : `${ADMIN_COURSE_EVENT_PREFIX}Enable Edit Mode`
          }
          kind="info"
          onAction={canEdit ? handleAddSection : handleEnableEditMode}
        >
          {canEdit
            ? t("home.courses.empty_course_banner_text")
            : t("home.courses.empty_course_banner_text_no_edit")}
        </Banner>
      ) : (
        <>
          {activeTermIds.map((termId) => (
            <CourseTermSectionPanel
              canEdit={canEdit}
              courseId={courseId}
              key={termId}
              termId={termId}
            />
          ))}

          {archivedTermIds.length > 0 && (
            <AccordionTitle
              headerClassName="ml-2 w-[calc(100%-12px)] justify-between border-b border-black-tint-90 pr-4"
              iconPosition="end"
              title={t("home.terms.archived_terms")}
            >
              {archivedTermIds.map((termId) => (
                <CourseTermSectionPanel
                  canEdit={canEdit}
                  courseId={courseId}
                  key={termId}
                  termId={termId}
                  isDisabled
                />
              ))}
            </AccordionTitle>
          )}
        </>
      )}
    </div>
  );
};
